import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, ArrowUpDown, X, ChevronDown, Check } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import ProductGrid from '../components/ProductGrid';

const SENSATIONS = ['Fresco', 'Floreale', 'Dolce', 'Pulito', 'Elegante', 'Energico'];
const CATEGORIES = [['bundle', 'Bundle'], ['all', 'Tutti'], ['lavapavimenti', 'Lavapavimenti'], ['sgrassatori', 'Sgrassatori'], ['professional', 'Professional']];
const SORTS = [
  ['bestseller', 'Più venduti'],
  ['new', 'Novità'],
  ['price-asc', 'Prezzo crescente'],
  ['price-desc', 'Prezzo decrescente'],
  ['rating', 'Migliori recensioni'],
];

export default function Shop({ defaultCategory = null, title = 'Tutti i prodotti ESSENYA', description = 'Scopri lavapavimenti, sgrassatori e detergenti profumati pensati per una casa pulita, ordinata e piacevole da vivere.' }) {
  const { products, fragrances, loading } = useProducts();
  const [params] = useSearchParams();
  const [category, setCategory] = useState(defaultCategory || params.get('category') || 'all');
  const [fragrance, setFragrance] = useState(params.get('fragrance') || 'all');
  const [sensation, setSensation] = useState('all');
  const [sort, setSort] = useState('bestseller');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = products.filter(p => !p.hidden);
    if (category !== 'all') list = list.filter(p => p.category === category);
    if (fragrance !== 'all') list = list.filter(p => p.fragrance === fragrance);
    if (sensation !== 'all') list = list.filter(p => p.sensation === sensation);
    if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
    if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    if (sort === 'new') list.sort((a, b) => (b.badge === 'Novità') - (a.badge === 'Novità'));
    if (sort === 'bestseller') list.sort((a, b) => b.reviews - a.reviews);
    return list;
  }, [products, category, fragrance, sensation, sort]);

  const activeChips = [
    fragrance !== 'all' && { key: 'fragrance', label: fragrances.find(f => f.slug === fragrance)?.name, clear: () => setFragrance('all') },
    sensation !== 'all' && { key: 'sensation', label: sensation, clear: () => setSensation('all') },
  ].filter(Boolean);

  const resetAll = () => { setFragrance('all'); setSensation('all'); };

  return (
    <main data-testid="shop-page" className="pt-12">
      <section className="container-essenya pb-8">
        <div className="text-eyebrow mb-3">Shop</div>
        <h1 className="font-display text-4xl md:text-5xl font-medium mb-4">{title}</h1>
        <p className="text-essenya-text text-lg max-w-2xl">{description}</p>
      </section>

      {/* Toolbar */}
      <section className="liquid-glass-panel border-x-0">
        <div className="container-essenya py-4 flex flex-wrap items-center gap-3">
          {/* Category pills */}
          <div data-testid="shop-category-tabs" className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
            {CATEGORIES.map(([v, l]) => (
              <button
                key={v}
                disabled={!!defaultCategory && v !== category}
                onClick={() => !defaultCategory && setCategory(v)}
                data-testid={`category-tab-${v}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-all duration-300 border ${
                  category === v ? 'bg-essenya-ink text-white border-essenya-ink' : 'bg-white text-essenya-ink border-essenya-border hover:border-essenya-ink'
                } ${!!defaultCategory && v !== category ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setFiltersOpen(o => !o)}
              data-testid="shop-filters-toggle"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-all duration-300 ${
                filtersOpen || activeChips.length ? 'bg-essenya-ink text-white border-essenya-ink' : 'bg-white text-essenya-ink border-essenya-border hover:border-essenya-ink'
              }`}
            >
              <SlidersHorizontal size={16} strokeWidth={1.5} /> Filtri
              {activeChips.length > 0 && <span className="bg-white text-essenya-ink rounded-full w-5 h-5 text-xs flex items-center justify-center">{activeChips.length}</span>}
            </button>

            <div className="relative">
              <ArrowUpDown size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <ChevronDown size={15} strokeWidth={1.5} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                value={sort}
                onChange={e => setSort(e.target.value)}
                data-testid="shop-sort"
                className="appearance-none bg-white border border-essenya-border rounded-full pl-9 pr-9 py-2 text-sm cursor-pointer hover:border-essenya-ink transition-colors"
              >
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Expandable filter panel */}
        {filtersOpen && (
          <div data-testid="shop-filter-panel" className="border-t border-essenya-border bg-white">
            <div className="container-essenya py-6 space-y-6">
              <ChipRow label="Fragranza" value={fragrance} setValue={setFragrance} options={[['all', 'Tutte'], ...fragrances.map(f => [f.slug, f.name])]} testId="chip-fragrance" />
              <ChipRow label="Sensazione" value={sensation} setValue={setSensation} options={[['all', 'Tutte'], ...SENSATIONS.map(s => [s, s])]} testId="chip-sensation" />
            </div>
          </div>
        )}
      </section>

      <section className="container-essenya py-8 pb-24">
        {/* Results + active chips */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <p className="text-sm text-essenya-text"><span data-testid="shop-results-count">{filtered.length}</span> prodotti</p>
          {activeChips.map(c => (
            <button key={c.key} onClick={c.clear} data-testid={`active-chip-${c.key}`} className="inline-flex items-center gap-1.5 bg-essenya-surface2 border border-essenya-border rounded-full pl-3 pr-2 py-1 text-xs hover:border-essenya-ink transition-colors">
              {c.label} <X size={13} strokeWidth={2} />
            </button>
          ))}
          {activeChips.length > 0 && (
            <button onClick={resetAll} data-testid="shop-clear-filters" className="text-xs text-essenya-text underline underline-offset-2 hover:text-essenya-ink">Cancella filtri</button>
          )}
        </div>

        {loading ? (
          <div data-testid="shop-loading" className="text-center py-20 text-essenya-text">Caricamento prodotti…</div>
        ) : filtered.length > 0 ? (
          <ProductGrid products={filtered} />
        ) : (
          <div data-testid="shop-empty" className="text-center py-20">
            <p className="font-display text-2xl mb-3">Nessun prodotto trovato</p>
            <button onClick={resetAll} className="btn-secondary">Reimposta i filtri</button>
          </div>
        )}
      </section>
    </main>
  );
}

function ChipRow({ label, value, setValue, options, testId }) {
  return (
    <div>
      <p className="text-eyebrow mb-3">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, l]) => (
          <button
            key={v}
            onClick={() => setValue(v)}
            data-testid={`${testId}-${v}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-all duration-300 ${
              value === v ? 'bg-essenya-ink text-white border-essenya-ink' : 'bg-white text-essenya-ink border-essenya-border hover:border-essenya-ink'
            }`}
          >
            {value === v && v !== 'all' && <Check size={14} strokeWidth={2} />}
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}
